import useUploadImageService from 'api/services/uploadImageService'
import { WizardStepFileProperties } from 'components/types'
import { resizeImageDisplay } from 'components/utils'
import { ChangeEvent, useEffect, useState } from 'react'
import { CustomColorsRef, IconSizes } from 'types/general'

import IconButton from '../iconButton/IconButton'
import Loading from '../loading/Loading'
import { useLoading } from '../loading/useLoading'

type ImageUploadProps = WizardStepFileProperties & {
  photo?: boolean
  video?: boolean
  file?: boolean
}

const ImageUpload = ({
  name,
  label,
  files,
  required,
  onChange,
  setIsValid,
  photo = true,
  video = false,
  file = false,
}: ImageUploadProps) => {
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const { isLoading, setLoading } = useLoading()
  const { uploadImage } = useUploadImageService()

  const imageContainer = document.getElementById(
    'upload-image-container'
  )?.clientWidth

  const validateFileType = (file: File): boolean => {
    if (!photo && !video && !file) return true
    
    const fileType = file.type
    const fileName = file.name.toLowerCase()
    
    let isValid = false
    
    if (photo) {
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      if (imageTypes.includes(fileType)) isValid = true
      if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || 
          fileName.endsWith('.png') || fileName.endsWith('.webp')) isValid = true
    }
    
    if (video) {
      const videoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov']
      if (videoTypes.includes(fileType)) isValid = true
      if (fileName.endsWith('.mp4') || fileName.endsWith('.webm') || 
          fileName.endsWith('.ogg') || fileName.endsWith('.avi') || fileName.endsWith('.mov')) isValid = true
    }
    
    if (file) {
      const fileExtensions = ['.pdf', '.doc', '.docx', '.txt', '.csv', '.xlsx']
      if (fileExtensions.some(ext => fileName.endsWith(ext))) isValid = true
    }
    
    return isValid
  }

  const getAcceptAttribute = () => {
    if (!photo && !video && !file) return 'image/*'
    
    const acceptedTypes: string[] = []
    if (photo) acceptedTypes.push('image/jpeg', 'image/jpg', 'image/png', 'image/webp')
    if (video) acceptedTypes.push('video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov')
    if (file) acceptedTypes.push('.pdf', '.doc', '.docx', '.txt', '.csv', '.xlsx')
    
    return acceptedTypes.join(',')
  }

  const uploadFiles = async (filesToUpload: File[]) => {
    setLoading(true)
    const formData = new FormData()
    filesToUpload.forEach(file => {
      formData.append('images[]', file)
    })

    try {
      const { urls } = await uploadImage(formData)
      const existingFiles = (files as string[]) || []
      const updatedFiles = [...existingFiles, ...urls]
      onChange({ name, value: updatedFiles })
    } catch (error) {
      console.error('Upload failed', error)
    } finally {
      setLoading(false)
    }
  }

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (fileList && fileList.length > 0) {
      const filesArray = Array.from(fileList)
      const validFiles = filesArray.filter(validateFileType)
      
      if (validFiles.length !== filesArray.length) {
        alert('Some files were rejected due to invalid file type')
      }
      
      if (validFiles.length > 0) {
        uploadFiles(validFiles)
      }
    }
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const fileList = e.dataTransfer.files
    if (fileList && fileList.length > 0) {
      const filesArray = Array.from(fileList)
      const validFiles = filesArray.filter(validateFileType)
      
      if (validFiles.length !== filesArray.length) {
        alert('Some files were rejected due to invalid file type')
      }
      
      if (validFiles.length > 0) {
        uploadFiles(validFiles)
      }
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const getDropZoneText = () => {
    if (!photo && !video && !file) return 'Click to upload or drag and drop images here'
    
    const types = []
    if (photo) types.push('Images')
    if (video) types.push('Videos')
    if (file) types.push('Files')
    
    if (types.length === 1) return `Click to upload or drag and drop ${types[0]} here`
    if (types.length === 2) return `Click to upload or drag and drop ${types[0]} and ${types[1]} here`
    return `Click to upload or drag and drop ${types.slice(0, -1).join(', ')} and ${types[types.length - 1]} here`
  }

  const handleDelete = (imageIndex: number) => {
    const newUrls = (files as string[]).filter(
      (_, index) => index !== imageIndex
    )
    onChange({ name, value: newUrls as unknown as string })
  }

  useEffect(() => {
    if (files && files.length > 0) {
      setSelectedImages(files as string[])
    } else {
      setSelectedImages([])
    }
  }, [files])

  useEffect(() => {
    if (!required || (files && files.length > 0)) {
      setIsValid?.(true)
    } else {
      setIsValid?.(false)
    }
  }, [files, setIsValid, required])

  return (
    <div className="mb-6">
      <div>
        <label
          htmlFor={name}
          className="block text-gray-700 font-bold mb-2 text-lg sm:text-xl"
        >
          {label}
        </label>
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => {
            if (!isLoading) {
              document.getElementById(name)?.click()
            }
          }}
          className={`w-full p-6 border-2 border-dashed rounded-md text-center transition duration-200 ${
            isLoading 
              ? 'border-imovPrimaryLight bg-imovPrimaryLight/10 cursor-not-allowed' 
              : 'border-gray-300 cursor-pointer hover:border-imovPrimaryLight'
          }`}
        >
          <p className={`${isLoading ? 'text-imovPrimary font-semibold' : 'text-gray-500'}`}>
            {isLoading ? 'Uploading...' : getDropZoneText()}
          </p>
        </div>
        <input
          name={name}
          id={name}
          type="file"
          accept={getAcceptAttribute()}
          multiple
          onChange={handleImageChange}
          className="hidden"
        />
      </div>
      <div id="upload-image-container" className="text-imovPrimary w-full py-6">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loading />
            <p className="text-gray-600 mt-2">Uploading images...</p>
          </div>
        ) : (
          !!selectedImages?.length && (
            <>
              <h1 className="block text-gray-700 font-bold">Loaded Images</h1>
              <div className="flex flex-wrap gap-1 md:gap-3 w-full justify-center py-2">
                {selectedImages.map((image, imageIndex) => {
                  return (
                    <div key={imageIndex} className="relative">
                      <img
                        src={image}
                        alt={`Preview ${imageIndex + 1}`}
                        style={{
                          width: resizeImageDisplay(imageContainer).width,
                          height: resizeImageDisplay(imageContainer).height,
                        }}
                        className="object-cover border border-gray-300 rounded-md shadow-lg"
                        onLoad={() => URL.revokeObjectURL(image)}
                      />
                      <IconButton
                        icon="regular-x"
                        size={IconSizes.SM}
                        handleClick={() => handleDelete(imageIndex)}
                        color={CustomColorsRef.Gray}
                        hoverColor={CustomColorsRef.PrimaryLight}
                        containerClassName="absolute top-1 right-1 rounded-full"
                        aria-label={`Delete image ${imageIndex + 1}`}
                      />
                    </div>
                  )
                })}
              </div>
            </>
          )
        )}
      </div>
    </div>
  )
}

export default ImageUpload
